import React from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import { useParamsToObj } from '../../hooks/useParamsToObj'

import Drawer from '../Drawer'
import Spinner from '../Spinner'
import LocationFiltersForm from './OffersFiltersForm'
import { OffersFilter } from '../../types/offers'

type FilterDrawerProps = {
  onClose: () => void
  open: boolean
}

const schema = Yup.object().shape({
  customers: Yup.array().of(Yup.object()),
  locations: Yup.array().of(Yup.object()),
  type: Yup.array().of(Yup.string()),
  nbClaimed: Yup.number(),
  cost: Yup.number(),
})

const FilterDrawer: React.FC<FilterDrawerProps> = (props) => {
  const navigate = useNavigate()
  const { counter, ...urlParams } = useParamsToObj<OffersFilter>([
    'locations',
    'type',
    'cost',
    'nbClaimed',
    'customers',
    'search',
  ])

  const initialFilter = {
    search: '',
    type: urlParams.type || [],
    customers: urlParams.customers || [],
    locations: urlParams.locations || [],
    nbClaimed: urlParams.cost || 0,
    cost: urlParams.nbClaimed || 0,
  }

  const handleOnSubmit = async (values: OffersFilter) => {
    try {
      const searchParams = createSearchParams()

      if (urlParams.search) searchParams.append('search', urlParams.search)

      if (values.type.length > 0) {
        searchParams.append('type', JSON.stringify(values.type))
      }

      if (values.locations.length > 0) {
        searchParams.append('locations', JSON.stringify(values.locations.map(({ id, name }) => ({ id, name }))))
      }
      if (values.customers.length > 0) {
        searchParams.append('customers', JSON.stringify(values.customers.map(({ id, name }) => ({ id, name }))))
      }

      if (values.cost) {
        searchParams.append('cost', values.cost.toString())
      }

      if (values.nbClaimed) {
        searchParams.append('nbClaimed', values.nbClaimed.toString())
      }

      navigate({
        search: `?${searchParams}`,
      })

      props.onClose()
    } catch (e: any) {
      console.log('🚀 ~ file: Locations/FilterDrawer.tsx ~ line 47 ~ handleOnSubmit ~ error', e)
    }
  }

  return (
    <Drawer open={props.open} onClose={props.onClose}>
      <Formik
        enableReinitialize
        initialValues={initialFilter}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleOnSubmit}
      >
        {({ isSubmitting, dirty }: FormikProps<OffersFilter>) => (
          <Drawer.FormContainer
            open={props.open}
            onClose={props.onClose}
            title="Filters"
            Footer={
              <>
                <button
                  className="btn btn-white"
                  onClick={() => {
                    navigate({
                      search: ``,
                    })

                    props.onClose()
                  }}
                  disabled={isSubmitting}
                  type="button"
                >
                  Reset
                </button>

                <button className="btn btn-primary ml-4" type="submit" disabled={isSubmitting || !dirty}>
                  {isSubmitting ? (
                    <>
                      <Spinner className="h-5 w-5 text-gray-700" aria-hidden="true" />
                      Loading
                    </>
                  ) : (
                    'Apply'
                  )}
                </button>
              </>
            }
          >
            <LocationFiltersForm isSubmitting={isSubmitting} />
          </Drawer.FormContainer>
        )}
      </Formik>
    </Drawer>
  )
}

export default FilterDrawer
